import { type UseQueryResult } from '@tanstack/react-query';
import { type PaginatedResponse, type WebsitePageDetail } from '@utazzo/shared';
import { UzButton } from '@utazzo/ui';
import { ExternalLink, Eye, EyeOff } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useCan } from '../lib';

import { NewPageDialog } from './components/NewPageDialog';
import {
  CUSTOM_PAGES_CONFIG,
  LANDING_PAGES_CONFIG,
  TOURISM_BOARDS_CONFIG,
  type PageScreenConfig,
} from './page-screen-config';
import { type PageListQuery } from './pages.api';
import { useDeletePage, usePagesList, usePublishPage, useUnpublishPage } from './use-pages';

import { type DataGridColumn } from '@/components/data-grid';
import { ListScreen } from '@/components/list-screen';
import { SoftPill } from '@/components/soft-pill';

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'PUBLISHED', label: 'Published' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'ARCHIVED', label: 'Archived' },
] as const;

/** Soft pill tone + dot per page status, mirroring the Users table style. */
const STATUS_PILL: Record<string, { tone: string; dot: string; label: string }> = {
  PUBLISHED: { tone: 'bg-emerald-50 text-emerald-700', dot: 'bg-emerald-500', label: 'Published' },
  DRAFT: { tone: 'bg-amber-50 text-amber-700', dot: 'bg-amber-500', label: 'Draft' },
  ARCHIVED: { tone: 'bg-zinc-100 text-zinc-600', dot: 'bg-zinc-400', label: 'Archived' },
};

const COLUMNS: ReadonlyArray<DataGridColumn<WebsitePageDetail>> = [
  {
    id: 'title',
    header: 'Title',
    sortable: true,
    cell: (row) => (
      <div className="flex flex-col">
        <span className="font-medium text-(--fg)">{row.title}</span>
        <span className="text-xs text-(--fg-faint)">/{row.slug}</span>
      </div>
    ),
  },
  {
    id: 'pageType',
    header: 'Type',
    cell: (row) => <span className="text-(--fg-muted)">{row.pageType}</span>,
  },
  {
    id: 'status',
    header: 'Status',
    sortable: true,
    cell: (row) => {
      const b = STATUS_PILL[row.status] ?? {
        tone: 'bg-zinc-100 text-zinc-600',
        dot: 'bg-zinc-400',
        label: row.status,
      };
      return (
        <SoftPill className={b.tone} dot dotClassName={b.dot}>
          {b.label}
        </SoftPill>
      );
    },
  },
  {
    id: 'updatedAt',
    header: 'Updated',
    sortable: true,
    cell: (row) => (
      <span className="text-(--fg-muted) tabular-nums">
        {new Date(row.updatedAt).toLocaleString()}
      </span>
    ),
  },
];

/**
 * Slugs that get their own dedicated sidebar entry and are therefore hidden
 * from the generic Custom Pages list to avoid two edit surfaces for one page.
 * 'career' is edited via the sidebar "Career" tab (see CareerEditor).
 */
const HIDDEN_SLUGS = new Set(['career']);

/**
 * Adapts usePagesList to the PaginatedResponse shape ListScreen expects.
 * Adds totalPages/hasNext/hasPrev derived from the raw pagination fields.
 * hasNext is used by fetchAllPages (export-all) in addition to ListScreen.
 * Also drops HIDDEN_SLUGS rows and reduces totalItems by however many were
 * removed from the fetched page so the "N of M" count stays consistent.
 */
function useAdaptedPagesList(
  query: PageListQuery,
  basePath: string,
): UseQueryResult<PaginatedResponse<WebsitePageDetail>> {
  const result = usePagesList(query, basePath);
  return useMemo(() => {
    if (!result.data) {
      return result as unknown as UseQueryResult<PaginatedResponse<WebsitePageDetail>>;
    }
    const { page, pageSize } = result.data.pagination;
    const data = result.data.data.filter((p) => !HIDDEN_SLUGS.has(p.slug));
    const removed = result.data.data.length - data.length;
    const totalItems = Math.max(0, result.data.pagination.totalItems - removed);
    const totalPages = Math.ceil(totalItems / pageSize);
    const adapted: PaginatedResponse<WebsitePageDetail> = {
      data,
      pagination: {
        page,
        pageSize,
        totalItems,
        totalPages,
        hasNext: page < totalPages,
        hasPrev: page > 1,
      },
    };
    return { ...result, data: adapted } as UseQueryResult<PaginatedResponse<WebsitePageDetail>>;
  }, [result]);
}

// One bound adapter hook per screen so `useList` has a stable identity and a
// fixed basePath (rules-of-hooks: the same hook must run every render). The
// screen picks its hook by basePath below.
const useCustomPagesList = (q: PageListQuery) =>
  useAdaptedPagesList(q, CUSTOM_PAGES_CONFIG.basePath);
const useTourismBoardsList = (q: PageListQuery) =>
  useAdaptedPagesList(q, TOURISM_BOARDS_CONFIG.basePath);
const useLandingPagesList = (q: PageListQuery) =>
  useAdaptedPagesList(q, LANDING_PAGES_CONFIG.basePath);

const LIST_HOOK_BY_BASE: Record<
  string,
  (q: PageListQuery) => UseQueryResult<PaginatedResponse<WebsitePageDetail>>
> = {
  [CUSTOM_PAGES_CONFIG.basePath]: useCustomPagesList,
  [TOURISM_BOARDS_CONFIG.basePath]: useTourismBoardsList,
  [LANDING_PAGES_CONFIG.basePath]: useLandingPagesList,
};

/**
 * Shared list screen for the three WebsitePage surfaces. Defaults to Custom
 * Pages; the router passes TOURISM_BOARDS_CONFIG / LANDING_PAGES_CONFIG for the
 * type-scoped screens (own endpoint, own permission module, own editor route).
 */
export const PagesListPage = ({
  config = CUSTOM_PAGES_CONFIG,
}: {
  config?: PageScreenConfig;
} = {}): React.JSX.Element => {
  const navigate = useNavigate();
  const canWrite = useCan(config.caps.write);
  const canPublish = useCan(config.caps.publish);
  const canDelete = useCan(config.caps.delete);

  const [newOpen, setNewOpen] = useState(false);

  const publish = usePublishPage(config.basePath);
  const unpublish = useUnpublishPage(config.basePath);
  const del = useDeletePage(config.basePath);

  const useList = LIST_HOOK_BY_BASE[config.basePath] ?? useCustomPagesList;

  return (
    <ListScreen<WebsitePageDetail, PageListQuery>
      title={config.title}
      description={config.description}
      useList={useList}
      defaultSort={{ by: 'updatedAt', dir: 'desc' }}
      defaultPageSize={20}
      toQuery={(state, base) => ({
        ...base,
        // A typed screen (tourism boards, landing pages...) is pinned to its own
        // type; only the generic list honours the deep-linked one.
        ...(config.listPageType
          ? { pageType: config.listPageType }
          : state.filters.pageType
            ? { pageType: state.filters.pageType }
            : {}),
        ...(state.filters.status ? { status: state.filters.status } : {}),
      })}
      columns={COLUMNS}
      filters={[
        {
          id: 'status',
          label: 'Status',
          options: STATUS_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
          widthClass: 'w-44',
        },
        // Deep-link targets for the website dashboard's breakdown tables. Hidden: no
        // chip, just a removable pill while one is active.
        {
          id: 'pageType',
          label: 'Page type',
          options: [
            { value: 'CUSTOM', label: 'Custom' },
            { value: 'STATIC', label: 'Static' },
          ],
          hidden: true,
        },
      ]}
      getRowId={(row) => row.id}
      onRowClick={(row) => navigate(`${config.editorBase}/${row.id}`)}
      createButton={
        canWrite ? { label: config.createLabel, onClick: () => setNewOpen(true) } : undefined
      }
      headerExtra={
        <UzButton variant="ghost" size="sm" onClick={() => window.open('/', '_blank')}>
          <ExternalLink className="mr-2 h-4 w-4" />
          View site
        </UzButton>
      }
      rowActions={{
        extra: (row) => [
          {
            type: 'item',
            label: 'Edit',
            onSelect: () => navigate(`${config.editorBase}/${row.id}`),
          },
          ...(canPublish
            ? [
                row.status === 'PUBLISHED'
                  ? {
                      type: 'item' as const,
                      label: 'Unpublish',
                      icon: <EyeOff className="h-4 w-4" />,
                      onSelect: () => unpublish.mutate(row.id),
                    }
                  : {
                      type: 'item' as const,
                      label: 'Publish',
                      icon: <Eye className="h-4 w-4" />,
                      onSelect: () => publish.mutate(row.id),
                    },
              ]
            : []),
        ],
        delete: {
          onDelete: (row) => del.mutateAsync(row.id),
          disabled: () => !canDelete,
          confirm: (row) => ({
            title: 'Delete this page?',
            description: `"${row.title}" (/${row.slug}) will be soft-deleted and removed from the public site.`,
          }),
        },
      }}
      overlays={<NewPageDialog open={newOpen} onOpenChange={setNewOpen} config={config} />}
      copy={{
        searchPlaceholder: 'Search by title or slug',
        emptyTitle: config.emptyTitle,
        emptyDescription: config.emptyDescription,
        errorTitle: 'Failed to load pages',
        errorDescription: 'Try refreshing or check your connection.',
      }}
    />
  );
};
